import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightbulb } from "lucide-react";
import {
  getConsent,
  setConsent,
  OPEN_PREFS_EVENT,
  useCookieBannerEnabled,
} from "@/lib/consent";
import { trackView } from "@/lib/track";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

export const CookieConsent = () => {
  const bannerEnabled = useCookieBannerEnabled();
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    if (!bannerEnabled) {
      setVisible(false);
      return;
    }
    const existing = getConsent();
    if (existing) {
      setAnalytics(existing.analytics);
    } else {
      const t = setTimeout(() => setVisible(true), 700);
      return () => clearTimeout(t);
    }
  }, [bannerEnabled]);

  useEffect(() => {
    const openPrefs = () => {
      if (!bannerEnabled) return;
      setAnalytics(getConsent()?.analytics ?? true);
      setPrefsOpen(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, openPrefs);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, openPrefs);
  }, [bannerEnabled]);

  const acceptAndTrack = (allow) => {
    setConsent(allow);
    if (allow) trackView(window.location.pathname);
    setVisible(false);
    setPrefsOpen(false);
  };

  const openPrefsFromBanner = () => {
    setVisible(false);
    setPrefsOpen(true);
  };

  const onPrefsOpenChange = (open) => {
    setPrefsOpen(open);
    // Closed without deciding (X / click outside) — bring the banner back so
    // there's still a way to answer, unless a decision already exists.
    if (!open && !getConsent()) setVisible(true);
  };

  if (!bannerEnabled) return null;
  if (!visible && !prefsOpen) return null;

  return (
    <>
      {visible && (
        <div
          data-testid="cookie-consent-banner"
          className="fixed z-[60] bottom-3 left-3 right-3 md:right-auto md:left-6 md:bottom-6 md:max-w-md animate-fade-up"
        >
          <div className="relative bg-cream border-control border-stone rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.25)] p-6 pt-8 overflow-hidden">
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gradient-to-br from-gold to-copper opacity-90 flex items-end justify-start">
              <Lightbulb className="w-9 h-9 text-ink/80 mb-4 ml-4" strokeWidth={1.6} />
            </div>

            <p className="font-sans text-xs uppercase tracking-[0.2em] text-ash mb-1.5">Salut c'est nous…</p>
            <h2 className="font-serif text-2xl font-medium text-ink leading-tight mb-3 pr-14">
              les Cookies !
            </h2>
            <p className="text-sm text-ink/70 leading-relaxed mb-5">
              On a attendu d'être sûrs que le contenu de ce site vous intéresse avant de vous
              déranger, mais on aimerait bien vous accompagner pendant votre visite… C'est OK
              pour vous ?{" "}
              <Link to="/confidentialite" className="underline underline-offset-2 hover:text-copper">
                En savoir plus
              </Link>
            </p>

            <div className="flex flex-col sm:flex-row gap-2.5">
              <button
                data-testid="cookie-refuse-all"
                onClick={() => acceptAndTrack(false)}
                className="text-xs uppercase tracking-[0.1em] text-ash hover:text-ink transition-colors px-3 py-2.5 order-3 sm:order-1"
              >
                Non merci
              </button>
              <button
                data-testid="cookie-customize"
                onClick={openPrefsFromBanner}
                className="flex-1 text-xs uppercase tracking-[0.1em] border border-ink/20 text-ink hover:border-ink transition-colors rounded-full px-4 py-2.5 order-2"
              >
                Je choisis
              </button>
              <button
                data-testid="cookie-accept-all"
                onClick={() => acceptAndTrack(true)}
                className="flex-1 text-xs uppercase tracking-[0.1em] bg-ink text-cream hover:bg-gold hover:text-ink transition-colors rounded-full px-4 py-2.5 order-1 sm:order-3 font-medium"
              >
                OK pour moi
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={prefsOpen} onOpenChange={onPrefsOpenChange}>
        <DialogContent className="bg-cream border-stone sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">Vos préférences</DialogTitle>
            <DialogDescription className="text-ink/60">
              Choisissez ce que vous acceptez de partager. Vous pourrez changer d'avis à tout
              moment depuis le pied de page du site.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 rounded-xl border-control border-stone bg-input-fill/40 p-4">
              <div>
                <p className="text-sm font-medium text-ink">Panier & session</p>
                <p className="text-xs text-ink/60 mt-1">
                  Nécessaire pour garder votre panier et rester connecté. Toujours actif.
                </p>
              </div>
              <Switch checked disabled className="data-[state=checked]:bg-ash" />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-xl border-control border-stone p-4">
              <div>
                <p className="text-sm font-medium text-ink">Mesure d'audience</p>
                <p className="text-xs text-ink/60 mt-1">
                  Pages visitées, appareil et position approximative, pour améliorer le site et
                  ses performances. Si désactivé, votre visite reste anonyme : rien n'est gardé
                  d'une visite à l'autre.
                </p>
              </div>
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                data-testid="cookie-toggle-analytics"
                className="data-[state=checked]:bg-gold"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <button
              onClick={() => acceptAndTrack(false)}
              className="text-xs uppercase tracking-[0.1em] text-ash hover:text-ink transition-colors px-4 py-2.5"
            >
              Tout refuser
            </button>
            <button
              data-testid="cookie-save-prefs"
              onClick={() => acceptAndTrack(analytics)}
              className="text-xs uppercase tracking-[0.1em] bg-ink text-cream hover:bg-gold hover:text-ink transition-colors rounded-full px-5 py-2.5 font-medium"
            >
              Enregistrer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
